/**
 * Vlastný rozbaľovací zoznam variantov.
 *
 * Natívny <select> ostáva v stránke ako zdroj pravdy: téma (VariantSelects
 * v global.js) z neho číta hodnoty a formulár ho odosiela. Tento prvok ho len
 * skryje, namiesto neho ukáže tlačidlo s rovnakými triedami, teda rovnakým
 * vzhľadom, a pri výbere nastaví hodnotu selectu a vyvolá `change` – presne
 * to, čo by spravil zákazník v natívnom zozname. Logika témy sa nemení.
 *
 * Čo natívny zoznam nevie: vypredané položky sú zošednuté a v poli poslednej
 * možnosti majú zvonček strážneho psa. Klik na zvonček rieši dex-wishlist.js
 * (delegovane podľa [data-dex-bell]), tu sa len vykreslí.
 *
 * Ovládanie podľa vzoru WAI-ARIA „select-only combobox": fokus ostáva na
 * tlačidle, aktívnu položku hlási aria-activedescendant.
 *
 * Bez JS sa prvok neaktivuje a ostáva natívny select.
 */
(function () {
  'use strict';

  // Quick view vkladá skripty znova pri každom otvorení.
  if (customElements.get('dex-variant-combobox')) return;

  var CHECK_SVG =
    '<svg viewBox="0 0 24 24" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5" ' +
    'stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  class DexVariantCombobox extends HTMLElement {
    connectedCallback() {
      if (this.button) {
        document.addEventListener('variant:change', this.onVariantChange);
        return;
      }

      var select = this.querySelector('select');
      if (!select) return;

      this.select = select;
      this.picker = this.closest('variant-selects');
      var wrapper = this.closest('[data-option-index]');
      this.optionIndex = wrapper ? Number(wrapper.dataset.optionIndex.replace('option', '')) - 1 : 0;
      // Zvonček v poli je len pri poslednej možnosti – až tá určí konkrétny
      // variant. Zo zoznamu sa preto dá sledovať len tam a berie si z neho
      // handle produktu, miesto pre poznámku aj ikonu.
      this.fieldBell = this.querySelector('[data-dex-bell][data-follows-variant]');

      var base = select.id || 'DexCombobox-' + Math.random().toString(36).slice(2);
      var label = select.id ? document.querySelector('label[for="' + select.id + '"]') : null;
      if (label && !label.id) label.id = base + '-label';

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'select__select dex-cb__button';
      button.setAttribute('role', 'combobox');
      button.setAttribute('aria-haspopup', 'listbox');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', base + '-list');
      if (label) button.setAttribute('aria-labelledby', label.id);
      button.innerHTML = '<span class="dex-cb__value"></span>';

      var list = document.createElement('ul');
      list.id = base + '-list';
      list.className = 'dex-cb__list';
      list.setAttribute('role', 'listbox');
      list.tabIndex = -1;
      list.hidden = true;
      if (label) list.setAttribute('aria-labelledby', label.id);

      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      select.after(button, list);

      this.button = button;
      this.list = list;
      this.classList.add('is-ready');

      button.addEventListener('click', () => (this.expanded ? this.close(true) : this.open()));
      button.addEventListener('keydown', (e) => this.onKeydown(e));
      // Medzerník aktivuje tlačidlo až pri keyup – bez toho by zoznam, ktorý
      // keydown práve zavrel, hneď znova otvoril.
      button.addEventListener('keyup', (e) => {
        if (e.key === ' ') e.preventDefault();
      });

      // Klik do zoznamu nesmie zobrať fokus tlačidlu.
      list.addEventListener('mousedown', (e) => e.preventDefault());
      list.addEventListener('click', (e) => this.pick(e.target.closest('[role="option"]')));
      // Zvýraznenie ide za myšou, ako v natívnom zozname.
      list.addEventListener('mousemove', (e) => {
        var item = e.target.closest('[role="option"]');
        if (item && !item.classList.contains('is-active')) {
          this.setActive(this.items().indexOf(item), false);
        }
      });

      // Klik na <label> fokusuje skrytý select – presmerujeme na tlačidlo.
      select.addEventListener('focus', () => button.focus());
      select.addEventListener('change', () => this.sync());

      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.close();
      });
      // iOS tlačidlám pri ťuknutí fokus nedáva, focusout by neprišiel.
      this.onOutside = (e) => {
        if (!this.contains(e.target)) this.close();
      };
      // Lepiaci košík mení hodnotu selectu priamo, bez udalosti, a až po
      // variant:change. Preto synchronizácia o takt neskôr.
      this.onVariantChange = () => setTimeout(() => this.sync(), 0);
      document.addEventListener('variant:change', this.onVariantChange);

      this.sync();
    }

    disconnectedCallback() {
      if (!this.button) return;
      document.removeEventListener('variant:change', this.onVariantChange);
      document.removeEventListener('pointerdown', this.onOutside, true);
    }

    get expanded() {
      return this.button.getAttribute('aria-expanded') === 'true';
    }

    items() {
      return Array.from(this.list.children);
    }

    activeIndex() {
      return this.items().findIndex((el) => el.classList.contains('is-active'));
    }

    /** Variant, ktorý by vznikol výberom `value` pri ostatných možnostiach, ako sú. */
    variantFor(value) {
      if (!this.picker || !this.picker.getVariantData) return null;
      var wanted = Array.from(this.picker.querySelectorAll('select'), (s) => s.value);
      wanted[this.optionIndex] = value;
      return this.picker.getVariantData().find((v) => v.options.every((o, i) => o === wanted[i]));
    }

    sync() {
      var option = this.select.options[this.select.selectedIndex];
      this.button.firstChild.textContent = option ? option.textContent.trim() : '';
    }

    /** Zoznam sa skladá pri každom otvorení – dostupnosť závisí od ostatných možností. */
    render() {
      var list = this.list;
      list.textContent = '';

      Array.from(this.select.options).forEach((option, i) => {
        var variant = this.variantFor(option.value);
        var soldOut = !variant || !variant.available;

        var item = document.createElement('li');
        item.id = list.id + '-' + i;
        item.className = 'dex-cb__option' + (soldOut ? ' is-soldout' : '');
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(option.value === this.select.value));
        if (option.disabled) item.setAttribute('aria-disabled', 'true');
        item.dataset.value = option.value;

        var name = document.createElement('span');
        name.className = 'dex-cb__name';
        name.textContent = option.textContent.trim();
        item.appendChild(name);

        // Vypredaný variant so zvončekom dostane výzvu na upozornenie. Bez
        // zvončeka (iná než posledná možnosť) a pri kombinácii, ktorá ako
        // variant neexistuje, ostáva obyčajný štítok.
        var watchable = variant && !variant.available && this.fieldBell;

        if (soldOut && !watchable) {
          var status = document.createElement('span');
          status.className = 'dex-cb__status';
          status.textContent = variant ? 'Vypredané' : 'Nedostupné';
          item.appendChild(status);
        }

        if (!soldOut && option.value === this.select.value) {
          var check = document.createElement('span');
          check.className = 'dex-cb__check';
          check.setAttribute('aria-hidden', 'true');
          check.innerHTML = CHECK_SVG;
          item.appendChild(check);
        } else if (watchable) {
          // Výzva je pre čítačky skrytá, stav im teda povie tento text.
          var hint = document.createElement('span');
          hint.className = 'visually-hidden';
          hint.textContent = ', vypredané';
          item.appendChild(hint);

          var bell = document.createElement('span');
          bell.className = 'dex-cb__bell';
          bell.dataset.dexBell = '';
          bell.dataset.variantId = String(variant.id);
          bell.dataset.productHandle = this.fieldBell.dataset.productHandle;
          bell.dataset.noteId = this.fieldBell.dataset.noteId;
          // Interaktívny prvok vnútri položky zoznamu čítačky ovládať nevedia.
          // Ich cesta je zvonček v poli, ktorý sa po výbere vypredaného
          // variantu ukáže ako samostatné tlačidlo.
          bell.setAttribute('aria-hidden', 'true');
          // Text sa prepína podľa .is-active, ktorú nastavuje dex-wishlist.js.
          bell.innerHTML =
            this.fieldBell.querySelector('svg').outerHTML +
            '<span class="dex-cb__bell-off">Informovať o naskladnení</span>' +
            '<span class="dex-cb__bell-on">Dáme vám vedieť</span>';
          item.appendChild(bell);
        }

        list.appendChild(item);
      });
    }

    open() {
      if (this.expanded) return;
      this.render();
      this.list.hidden = false;
      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', this.onOutside, true);
      var selected = this.items().findIndex((el) => el.getAttribute('aria-selected') === 'true');
      this.setActive(Math.max(selected, 0));
    }

    close(focus) {
      if (this.expanded) {
        this.list.hidden = true;
        this.classList.remove('is-open');
        this.button.setAttribute('aria-expanded', 'false');
        this.button.removeAttribute('aria-activedescendant');
        document.removeEventListener('pointerdown', this.onOutside, true);
      }
      if (focus) this.button.focus();
    }

    setActive(index, scroll = true) {
      var items = this.items();
      var item = items[Math.max(0, Math.min(index, items.length - 1))];
      if (!item) return;
      items.forEach((el) => el.classList.toggle('is-active', el === item));
      this.button.setAttribute('aria-activedescendant', item.id);
      if (!scroll) return;

      // Posun len vnútri zoznamu – scrollIntoView by hýbal aj stránkou.
      var list = this.list;
      if (item.offsetTop < list.scrollTop) {
        list.scrollTop = item.offsetTop;
      } else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
        list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
      }
    }

    /**
     * Výber položky. Klik na zvonček variant tiež vyberie – zákazník tak vidí,
     * čoho sa upozornenie týka, a poznámka pod poľom dáva zmysel. Samotné
     * zapnutie strážneho psa potom spraví dex-wishlist.js, ktorého
     * delegovaný handler na dokumente beží až po tomto.
     */
    pick(item) {
      if (!item || item.getAttribute('aria-disabled') === 'true') return;
      var value = item.dataset.value;
      if (this.select.value !== value) {
        this.select.value = value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.close(true);
    }

    onKeydown(e) {
      var open = this.expanded;

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          e.preventDefault();
          if (!open) return this.open();
          if (e.altKey && e.key === 'ArrowUp') return this.pick(this.items()[this.activeIndex()]);
          return this.setActive(this.activeIndex() + (e.key === 'ArrowDown' ? 1 : -1));
        case 'Home':
        case 'End':
          e.preventDefault();
          if (!open) this.open();
          return this.setActive(e.key === 'Home' ? 0 : this.items().length - 1);
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!open) return this.open();
          return this.pick(this.items()[this.activeIndex()]);
        case 'Escape':
          if (!open) return;
          e.preventDefault();
          return this.close(true);
        case 'Tab':
          return this.close();
      }
    }
  }

  customElements.define('dex-variant-combobox', DexVariantCombobox);
})();
